import React, {useState, useEffect} from 'react';
import {useHistory, useParams, useLocation} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {ProgressBar} from 'primereact/progressbar';
import {Form, Field} from 'react-final-form';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {classNames} from "primereact/utils";
import {addLocale} from "primereact/api";
import moment from "moment";
import axios from "axios";
import {InputNumber} from "primereact/inputnumber";
import {AutoComplete} from "primereact/autocomplete";
import {Checkbox} from "primereact/checkbox";
import {applySchemaProductionDefaults, applyTechnologyDefaultUnit} from "../shared/productionItemDefaults";

const OfferitemFormComponent = (props) => {
  const {t} = props;
  const history = useHistory();
  const location = useLocation();
  const {id} = useParams();

  const [loading, setLoading] = useState(false);
  const [priceCalcLoading, setPriceCalcLoading] = useState({}); 
  const [schema, setSchema] = useState({});
  const [options, setOptions] = useState({});
  const [itemOptions, setItemOptions] = useState({});
  const [materialOptions, setMaterialOptions] = useState({});
  const [materialDisplay, setMaterialDisplay] = useState({});
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [parentCurrency, setParentCurrency] = useState(null);
  const [draggedProductionIndex, setDraggedProductionIndex] = useState(null);
  const [productionReorderLoading, setProductionReorderLoading] = useState(false);

  const [formData, setFormData] = useState({
    offer: Number(id),
    project_item_schema: Number(window.App.urlVars().offerItemSchemaId),
    project_item: {},
    production_items: [],
    materials: []
  });
  const [newFormData, setNewFormData] = useState({
    production_items: [],
    materials: []
  });

  const [prodRow, setProdRow] = useState({});
  const [matRow, setMatRow] = useState({});

  addLocale('hu', {
    firstDayOfWeek: 1,
    dayNames: ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'],
    dayNamesShort: ['vas', 'hé', 'ke', 'sze', 'csü', 'pé', 'szo'],
    dayNamesMin: ['V', 'H', 'K', 'SZ', 'CS', 'P', 'SZ'],
    monthNames: ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember',
      'október', 'november', 'december'],
    monthNamesShort: ['jan', 'feb', 'mar', 'ápr', 'máj', 'jun', 'júl', 'aug', 'szep', 'okt', 'nov', 'dec'],
    today: 'Ma',
    clear: 'Töröl'
  });

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const getId = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'object') return v.id ?? null;
    return v;
  };

  const materialFieldNameMap = {
    dimension_x: 'dimensionX',
    dimension_y: 'dimensionY',
    dimension_z: 'dimensionZ',
  };

  const getMaterialFieldValue = (row, fieldName) => {
    return row?.[fieldName] ?? row?.[materialFieldNameMap[fieldName]];
  };

  const setMaterialFieldValue = (row, fieldName, value) => {
    const mappedFieldName = materialFieldNameMap[fieldName];
    const nextRow = { ...(row || {}), [fieldName]: value };

    if (mappedFieldName) {
      nextRow[mappedFieldName] = value;
    }

    return nextRow;
  };

  const isFormFieldValid = (meta) => !!(meta.touched && meta.error);
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>;
  const validate = (data) => {
    let errors = {};
    return errors;
  };

  const isValueFilled = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    return true;
  };

  const getEditableFields = (fields) => {
    return (fields || []).filter(f => f.readonly !== true);
  };

  const isRowValidForAdd = (rowData, fields) => {
    const requiredFields = getEditableFields(fields).filter(f => f.required === true);

    return requiredFields.every(f => isValueFilled(rowData?.[f.name]));
  };

  const requiredLabel = (field) => (
    <>
      {field.label}
      {field.required === true && <span className="p-error"> *</span>}
    </>
  );

  const onSubmit = () => {
    setLoading(true);
    const vars = window.App.urlVars();
    const offerItemId = vars?.offerItemId;
    if (offerItemId) {
      apiCall('put', '/api/v2/offer-item/' + offerItemId, newFormData)
        .then(() => {
          window.App.toastShow('success', 'Sikeres mentés!');
          window.App.setState({rerenderOfferitemList: Math.random()});
          history.push(props.backlink || ('/project/offer/read/' + id));
        })
        .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
        .finally(() => setLoading(false));
    } else {
      apiCall('post', '/api/v2/offer-item', formData)
        .then(() => {
          window.App.toastShow('success', 'Sikeres mentés!');
          window.App.setState({rerenderOfferitemList: Math.random()});
          history.push(props.backlink || ('/project/offer/read/' + id));
        })
        .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
        .finally(() => setLoading(false));
    }
  };

  const buildCleanBacklink = () => {
    const p = new URLSearchParams(location.search || '');
    p.delete('search');
    p.delete('materialId');
    p.delete('materialName');
    p.delete('materialRowIndex');
    p.delete('backlink');
    const qs = p.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  };

  const openMaterialSearch = (rowIndex) => {
    const p = new URLSearchParams(location.search || '');
    p.set('search', 'material');
    p.set('materialRowIndex', String(rowIndex));
    p.set('backlink', buildCleanBacklink());
    p.set('status', 'ACTIVE');
    p.set('lock_status', '1');
    history.push(location.pathname + `?${p.toString()}`);
  };

  const searchMaterials = (e) => {
    const qRaw = (e.query || '').trim();
    const q = qRaw.toLowerCase();
    if (!q) {
      setMaterialSuggestions([]);
      return;
    }
    const value = encodeURIComponent(`%${qRaw}%`);
    apiCall('get', `/api/v2/material?item_per_page=50&search[name][operator]=like&search[name][value][]=${value}&search[status][value]=ACTIVE`)
      .then(r => {
        const items = r?.data?.data?.items || [];
        const filtered = items.filter(it => String(it?.name || '').toLowerCase().includes(q));
        setMaterialSuggestions(filtered);
      })
      .catch(() => setMaterialSuggestions([]));
  };

  const updateProductionRowField = (rowIndex, fieldName, value) => {
    setFormData(prev => {
      const next = [...(prev.production_items || [])];
      next[rowIndex] = { ...(next[rowIndex] || {}), [fieldName]: value };
      return { ...prev, production_items: next };
    });
    setNewFormData(prev => {
      const next = [...(prev.production_items || [])];
      next[rowIndex] = { ...(next[rowIndex] || {}), [fieldName]: value };
      return { ...prev, production_items: next };
    });
  };

  const normalizeProductionItems = (items) => (items || []).map((row, index) => ({
    ...row,
    position: index + 1
  }));

  const setProductionItemsState = (items) => {
    const normalized = normalizeProductionItems(items);
    setFormData(prev => ({...prev, production_items: normalized}));
    setNewFormData(prev => ({...prev, production_items: normalized}));
    return normalized;
  };

  const reorderProductionItems = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex === null || toIndex === null) return;

    const currentItems = [...(formData.production_items || [])];
    if (!currentItems[fromIndex] || !currentItems[toIndex]) return;

    const previousItems = currentItems.map(row => ({...row}));
    const [movedItem] = currentItems.splice(fromIndex, 1);
    currentItems.splice(toIndex, 0, movedItem);
    const normalizedItems = setProductionItemsState(currentItems);

    const offerItemId = window.App.urlVars().offerItemId;
    const canPersistOrder = !!offerItemId
      && normalizedItems.length > 0
      && normalizedItems.every(row => !!row?.id);
    if (!canPersistOrder) return;

    try {
      setProductionReorderLoading(true);
      await apiCall('post', `/api/v2/offer-item/${offerItemId}/production-items/reorder`, {
        production_item_ids: normalizedItems.map(row => Number(row.id))
      });
    } catch (error) {
      setProductionItemsState(previousItems);
      window.App.toastShow('error', t('error'), error.toString(), '', error.response);
    } finally {
      setProductionReorderLoading(false);
    }
  };

  const buildPriceListUrl = ({ currency, technologyId, unitId, now }) => {
    const p = new URLSearchParams();
    p.set('search[currency][operator]', '=');
    p.set('search[currency][value]', currency);

    p.set('search[technology][operator]', '=');
    p.set('search[technology][value]', String(technologyId));

    p.set('search[unit][operator]', '=');
    p.set('search[unit][value]', String(unitId));

    p.set('search[valid_from][operator]', '<=');
    p.set('search[valid_from][value]', now);

    p.set('search[valid_until][operator]', '>=');
    p.set('search[valid_until][value]', now);

    return `/api/v2/price-list-item?${p.toString()}`;
  };

  const applyPriceListCalculator = async (rowIndex) => {
    const isNew = rowIndex === 'new';
    const row = isNew ? (prodRow || {}) : ((formData.production_items || [])[rowIndex] || {});
    const quantity = Number(row.quantity ?? 0);
    const technologyId = row?.technology?.id || row?.technology;
    const unitId = row?.unit?.id || row?.unit;
    const currency = parentCurrency || formData?.currency || null;
    if (!currency) return window.App.toastShow('warn', t('missingData'), t('selectTechnologyWarning'));
    if (!unitId) return window.App.toastShow('warn', t('missingData'), t('selectUnitWarning'));
    if (!quantity || Number.isNaN(quantity)) return window.App.toastShow('warn', 'Hiányzó adat', 'Adj meg mennyiséget.');
    const now = moment().format('YYYY-MM-DD');
    const url = buildPriceListUrl({ currency, technologyId, unitId, now });
    try {
      setPriceCalcLoading(prev => ({ ...prev, [rowIndex]: true }));
      const response = await apiCall('get', url);
      const items = response?.data?.data?.items || response?.data?.data || [];
      const first = Array.isArray(items) ? items[0] : null;
      const price = Number(first?.price);
      if (!first || Number.isNaN(price)) {
        return window.App.toastShow('warn', t('noResults'));
      }
      //const netAmount = quantity * price;
      const netAmount = price;
      if (isNew) {
        setProdRow(prev => ({ ...prev, unit_price: netAmount }));
      } else {
        updateProductionRowField(rowIndex, 'unit_price', netAmount);
      }
    } catch (error) {
      window.App.toastShow('error', t('error'), error.toString(), '', error?.response);
    } finally {
      setPriceCalcLoading(prev => ({ ...prev, [rowIndex]: false }));
    }
  };

  useEffect(() => {
    const vars = window.App.urlVars();
    const isUpdate = !!vars?.offerItemId;
    if (isUpdate) return;
    apiCall('get', '/api/v2/network/settings')
      .then(response => {
        const settings = response?.data?.data || {};
        const defaultUnit = getId(settings?.default_unit);
        const defaultVatRate = getId(settings?.default_vat_rate);
        setFormData(prev => ({
          ...prev,
          project_item: {
            ...(prev.project_item || {}),
            ...(defaultUnit ? { unit: defaultUnit } : {}),
            ...(defaultVatRate ? { vat_rate: defaultVatRate } : {})
          }
        }));
        setNewFormData(prev => ({
          ...prev,
          project_item: {
            ...(prev.project_item || {}),
            ...(defaultUnit ? { unit: defaultUnit } : {}),
            ...(defaultVatRate ? { vat_rate: defaultVatRate } : {})
          }
        }));
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    const vars = window.App.urlVars();
    const schemaId = vars?.offerItemSchemaId;
    if (!schemaId || !id || id === 'undefined') return;
    setLoading(true);
    apiCall('get', '/api/v2/project-item-schema/' + schemaId)
      .then(response => {
        setSchema(response.data);
        if (!vars?.offerItemId) {
          const defaults = response?.data?.productionItems?.defaults || [];
          setFormData(prev => applySchemaProductionDefaults(prev, defaults));
          setNewFormData(prev => applySchemaProductionDefaults(prev, defaults));
        }
        response?.data?.projectItem?.form?.fields?.forEach((field) => {
          if (field.type === 'select') {
            apiCall('get', field?.options?.endpoint)
              .then(r => setOptions(prev => ({...prev, [field.name]: r?.data?.data?.items || []})))
              .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
          }
        });
        response?.data?.productionItems?.itemForm?.fields?.forEach((field) => {
          if (field.type === 'select') {
            apiCall('get', field?.options?.endpoint)
              .then(r => setItemOptions(prev => ({...prev, [field.name]: r?.data?.data?.items || []})))
              .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
          }
        });
        response?.data?.materials?.itemForm?.fields?.forEach((field) => {
          if (field.type === 'select') {
            apiCall('get', field?.options?.endpoint)
              .then(r => setMaterialOptions(prev => ({...prev, [field.name]: r?.data?.data?.items || []})))
              .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
          }
        });
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));

    if (vars?.offerItemId) {
      setLoading(true);
      apiCall('get', '/api/v2/offer-item/' + vars.offerItemId)
        .then(async response => {
          const productionItems = [...(response?.data?.data?.production_items || [])].sort((a, b) => {
            const aPosition = Number(a?.position ?? Number.MAX_SAFE_INTEGER);
            const bPosition = Number(b?.position ?? Number.MAX_SAFE_INTEGER);
            if (aPosition !== bPosition) return aPosition - bPosition;
            return Number(a?.id || 0) - Number(b?.id || 0);
          });
          setFormData({
            ...response.data.data,
            offer: Number(id),
            production_items: productionItems,
          });
          setNewFormData({
            ...response.data.data,
            offer: Number(id),
            production_items: productionItems,
          });
          try {
            const schemaId = Number(vars?.offerItemSchemaId);
            const by = await apiCall('get', `/api/v2/offer-item/${id}/by-offer-and-schema`);
            const arr = by?.data?.data || [];
            const schemaBlock = arr.find(x => Number(x?.schema?.id) === schemaId);
            const item = schemaBlock?.items?.find(it => Number(it?.id) === Number(vars.offerItemId));

            const display = {};
            (item?.materials || []).forEach((m, idx) => {
              display[idx] = m?.material || '';
            });

            setMaterialDisplay(prev => ({ ...prev, ...display }));
          } catch (error) {
            window.App.toastShow('error', t('error'), error.toString(), '', error.response)
          }
        })
        .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
        .finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    const vars = window.App.urlVars();
    if (!vars?.materialId || vars?.materialRowIndex === undefined || vars?.materialRowIndex === null) return;
    const materialId = Number(vars.materialId);
    const materialName = decodeURIComponent(String(vars.materialName || '').replace(/\+/g, ' '));
    const rowIndexRaw = String(vars.materialRowIndex);
    if (rowIndexRaw === 'new') {
      setMaterialDisplay(prev => ({...prev, new: materialName}));
      setMatRow(prev => ({...prev, material: materialId}));
      return;
    }
    const rowIndex = Number(rowIndexRaw);
    if (Number.isNaN(rowIndex)) return;
    setMaterialDisplay(prev => ({...prev, [rowIndex]: materialName}));
    setFormData(prev => {
      const materials = [...(prev.materials || [])];
      materials[rowIndex] = {...(materials[rowIndex] || {}), material: materialId};
      return {...prev, materials};
    });
    setNewFormData(prev => {
      const materials = [...(prev.materials || [])];
      materials[rowIndex] = {...(materials[rowIndex] || {}), material: materialId};
      return {...prev, materials};
    });
  }, [location.search]);

  useEffect(() => {
    if (!id || id === 'undefined') return;
    apiCall('get', '/api/v2/offer/' + id)
      .then(response => {
        setParentCurrency(response?.data?.data?.currency || null);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
  }, [id]);

  return (
    <div className={"tmp-form widget"}>
      {loading &&
        <ProgressBar mode="indeterminate" style={{height: '5px', position: "absolute", top: 0, left: 0, borderRadius: 0}} />
      }
      <div className={"p-grid"}>
        <div className={"p-col-12 p-lg-6 p-xl-6"}>
          <h3 className={"p-m-0 p-p-0"}>
            {schema?.projectItem?.form?.title || 'Alap adatok'}
          </h3>
        </div>
      </div>
      {loading === false &&
        <Form onSubmit={onSubmit} initialValues={formData} validate={validate} render={({handleSubmit}) => (
          <form onSubmit={handleSubmit}>
            <div className={"p-grid"}>
              {getEditableFields(schema?.projectItem?.form?.fields)?.map((field, index) => {
                if (field.type === 'bool') {
                  return (
                    <div key={field.name} className={"p-col-12 p-lg-6"}>
                      <Field name={field.name} render={({meta}) => (
                        <div className="p-field">
                          <label
                            htmlFor={field.name}
                            className={classNames({'p-error': isFormFieldValid(meta)})}
                          >
                            {requiredLabel(field)}
                          </label>

                          <div className="p-d-flex p-ai-center p-mt-2">
                            <Checkbox
                              inputId={field.name}
                              checked={!!formData?.project_item?.[field.name]}
                              className={classNames({'p-invalid': isFormFieldValid(meta)})}
                              onChange={(e) => {
                                const v = e.checked;

                                setFormData(prev => ({
                                  ...prev,
                                  project_item: {
                                    ...prev.project_item,
                                    [field.name]: v
                                  }
                                }));

                                setNewFormData(prev => ({
                                  ...prev,
                                  project_item: {
                                    ...(prev.project_item || {}),
                                    [field.name]: v
                                  }
                                }));
                              }}
                            />

                            <label htmlFor={field.name} className="p-ml-2 p-mb-0">
                              {field.placeholder || field.label}
                            </label>
                          </div>

                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  );
                }
                if (field.type === 'string') {
                  return (
                    <div key={field.name} className={"p-col-12 p-lg-6"}>
                      <Field name={field.name} render={({meta}) => (
                        <div className="p-field p-fluid">
                          <label htmlFor={field.name} className={classNames({'p-error': isFormFieldValid(meta)})}>{requiredLabel(field)}</label>
                          <span className="p-d-block">
                            {field.widget === 'textarea' ? <InputTextarea
                              id={field.name}
                              rows={4}
                              autoResize
                              value={formData?.project_item?.[field.name] || ''}
                              placeholder={field.placeholder}
                              className={classNames({'p-invalid': isFormFieldValid(meta)})}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFormData(prev => ({...prev, project_item: {...prev.project_item, [field.name]: v}}));
                                setNewFormData(prev => ({...prev, project_item: {...(prev.project_item || {}), [field.name]: v}}));
                              }}
                            /> : <InputText
                              id={field.name}
                              autoFocus={index === 0}
                              value={formData?.project_item?.[field.name] || ''}
                              placeholder={field.placeholder}
                              className={classNames({'p-invalid': isFormFieldValid(meta)})}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFormData(prev => ({...prev, project_item: {...prev.project_item, [field.name]: v}}));
                                setNewFormData(prev => ({...prev, project_item: {...(prev.project_item || {}), [field.name]: v}}));
                              }}
                            />}
                          </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  )
                }
                if (field.type === 'number') {
                  return (
                    <div key={field.name} className={"p-col-12 p-lg-6"}>
                      <Field name={field.name} render={({meta}) => (
                        <div className="p-field p-fluid">
                          <label htmlFor={field.name} className={classNames({'p-error': isFormFieldValid(meta)})}>{requiredLabel(field)}</label>
                          <span className="p-d-block">
                            <InputNumber
                              id={field.name}
                              mode="decimal"
                              minFractionDigits={0}
                              maxFractionDigits={2}
                              value={formData?.project_item?.[field.name]}
                              placeholder={field.placeholder}
                              className={classNames({'p-invalid': isFormFieldValid(meta)})}
                              onChange={(e) => {
                                const v = (e.value === null || e.value === undefined) ? null : Number(e.value);
                                setFormData(prev => ({...prev, project_item: {...prev.project_item, [field.name]: v}}));
                                setNewFormData(prev => ({...prev, project_item: {...(prev.project_item || {}), [field.name]: v}}));
                              }}
                            />
                          </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  )
                }
                if (field.type === 'select') {
                  return (
                    <div key={field.name} className={"p-col-12 p-lg-6"}>
                      <Field name={field.name} render={({meta}) => (
                        <div className="p-field p-fluid">
                          <label htmlFor={field.name} className={classNames({'p-error': isFormFieldValid(meta)})}>{requiredLabel(field)}</label>
                          <span className="p-d-block">
                            <Dropdown
                              id={field.name}
                              value={formData?.project_item?.[field.name]?.id || formData?.project_item?.[field.name] || null}
                              options={window.App.sortDropdownOptions(
                                options[field.name] || [],
                                field?.options?.labelField || "name"
                              )}
                              optionLabel={field?.options?.labelField || "name"}
                              optionValue="id"
                              emptyMessage={t('noSelectableItemYet')}
                              className={classNames({'p-error': isFormFieldValid(meta)})}
                              onChange={(e) => {
                                const v = e.value;
                                setFormData(prev => ({
                                  ...prev,
                                  project_item: {
                                    ...prev.project_item,
                                    [field.name]: v
                                  }
                                }));
                                setNewFormData(prev => ({
                                  ...prev,
                                  project_item: {
                                    ...(prev.project_item || {}),
                                    [field.name]: v
                                  }
                                }));
                              }}
                            />
                          </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  )
                }
                return null;
              })}
            </div>
            {schema?.productionItems?.enabled === true && (
            <>
            <div className={"p-grid p-mt-1"}>
              <div className={"p-col-12 p-lg-6 p-xl-6"}>
                <h3 className={"p-m-0 p-p-0"}>{schema?.productionItems?.itemForm?.title || 'Gyártás tételek'}</h3>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={"p-mt-0 w-100"} cellPadding={'5px'} cellSpacing={0}>
                <tr>
                  <th style={{width: '40px'}}></th>
                  {getEditableFields(schema?.productionItems?.itemForm?.fields)?.map(f => <th key={f.name}>{requiredLabel(f)}</th>)}
                  <th></th>
                </tr>
                {formData.production_items && formData.production_items[0] ? (
                  formData.production_items.map((row, rowIndex) => (
                    <tr
                      key={row.id || rowIndex}
                      onDragOver={(e) => {
                        if (draggedProductionIndex === null || draggedProductionIndex === rowIndex) return;
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedProductionIndex === null) return;
                        reorderProductionItems(draggedProductionIndex, rowIndex);
                        setDraggedProductionIndex(null);
                      }}
                      style={{opacity: draggedProductionIndex === rowIndex ? 0.55 : 1}}
                    >
                      <td className="p-text-center" style={{width: '40px'}}>
                        <span
                          draggable={!productionReorderLoading}
                          onDragStart={() => setDraggedProductionIndex(rowIndex)}
                          onDragEnd={() => setDraggedProductionIndex(null)}
                          style={{
                            cursor: productionReorderLoading ? 'not-allowed' : 'grab',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            opacity: productionReorderLoading ? 0.45 : 1
                          }}
                          title={t('reorderRows') || 'Sorrend módosítása'}
                        >
                          <i className="pi pi-bars"></i>
                        </span>
                      </td>
                      {getEditableFields(schema?.productionItems?.itemForm?.fields)?.map((f) => {
                        if (f.type === 'bool') {
                          return (
                            <td key={f.name} className="p-text-center">
                              <Checkbox
                                inputId={`production-${rowIndex}-${f.name}`}
                                checked={!!row[f.name]}
                                onChange={(e) => {
                                  const next = [...formData.production_items];

                                  next[rowIndex] = {
                                    ...next[rowIndex],
                                    [f.name]: e.checked
                                  };

                                  setFormData(prev => ({
                                    ...prev,
                                    production_items: next
                                  }));

                                  setNewFormData(prev => ({
                                    ...prev,
                                    production_items: next
                                  }));
                                }}
                              />
                            </td>
                          );
                        }
                        if (f.type === 'number') {
                          const hasPriceListCalc = Array.isArray(f.extras) && f.extras.includes('priceListCalculator');

                          return (
                            <td key={f.name}>
                              <div className={hasPriceListCalc ? "p-inputgroup" : ""}>
                                <InputNumber
                                  className="w-100"
                                  mode="decimal"
                                  minFractionDigits={0}
                                  maxFractionDigits={2}
                                  value={row[f.name]}
                                  placeholder={f.placeholder}
                                  onChange={(e) => {
                                    const v = (e.value === null || e.value === undefined) ? null : Number(e.value);
                                    const next = [...formData.production_items];
                                    next[rowIndex] = { ...next[rowIndex], [f.name]: v };
                                    setFormData(prev => ({ ...prev, production_items: next }));
                                    setNewFormData(prev => ({ ...prev, production_items: next }));
                                  }}
                                />

                                {hasPriceListCalc && (
                                  <Button
                                    type="button"
                                    icon={priceCalcLoading[rowIndex] ? "pi pi-spin pi-spinner" : "pi pi-dollar"}
                                    disabled={!!priceCalcLoading[rowIndex]}
                                    className="p-button-secondary"
                                    onClick={() => applyPriceListCalculator(rowIndex)}
                                    tooltip={t('priceListCalculator')}
                                    tooltipOptions={{ position: 'top' }}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        }

                        if (f.type === 'select') return (
                          <td key={f.name}>
                            <Dropdown
                              className="w-100"
                              value={row[f.name]}
                              options={window.App.sortDropdownOptions(
                                itemOptions[f.name] || [],
                                f?.options?.labelField || "name"
                              )}
                              optionLabel={f?.options?.labelField || "name"}
                              optionValue="id"
                              emptyMessage={t('noSelectableItemYet')}
                              onChange={(e) => {
                                const next = [...formData.production_items];
                                next[rowIndex] = applyTechnologyDefaultUnit(
                                  next[rowIndex],
                                  f.name,
                                  e.target.value,
                                  itemOptions.technology || []
                                );
                                setFormData(prev => ({...prev, production_items: next}));
                                setNewFormData(prev => ({...prev, production_items: next}));
                              }}
                            />
                          </td>
                        )
                        return <td key={f.name}></td>;
                      })}
                      <td className={"p-text-right"}>
                        <Button type="button" icon="pi pi-trash" className="p-button-danger"
                          onClick={() => {
                            const next = [...(formData.production_items || [])];
                            next.splice(rowIndex, 1);
                            setProductionItemsState(next);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={(getEditableFields(schema?.productionItems?.itemForm?.fields)?.length || 1) + 2} className={"p-text-center"}>
                      Még nincs tétel. Adj hozzá új tételt!
                    </td>
                  </tr>
                )}
                <tfoot className={"add"}>
                  <tr className={"add-spacer"}>
                    <td></td>
                    {getEditableFields(schema?.productionItems?.itemForm?.fields)?.map(f => <td key={f.name}><i className={"pi pi-arrow-up"} /></td>)}
                    <td></td>
                  </tr>
                  <tr className={"add-form"}>
                    <td></td>
                    {getEditableFields(schema?.productionItems?.itemForm?.fields)?.map((f) => {
                      if (f.type === 'bool') {
                        return (
                          <td key={f.name} className="p-text-center">
                            <Checkbox
                              inputId={`production-new-${f.name}`}
                              checked={!!prodRow[f.name]}
                              onChange={(e) => {
                                setProdRow(prev => ({
                                  ...prev,
                                  [f.name]: e.checked
                                }));
                              }}
                            />
                          </td>
                        );
                      }
                      if (f.type === 'number') {
                        const hasPriceListCalc = Array.isArray(f.extras) && f.extras.includes('priceListCalculator');
                        return (
                          <td key={f.name}>
                            <div className={hasPriceListCalc ? "p-inputgroup" : ""}>
                              <InputNumber
                                className="w-100"
                                mode="decimal"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                value={prodRow[f.name]}
                                placeholder={f.placeholder}
                                onChange={(e) =>
                                  setProdRow(prev => ({
                                    ...prev,
                                    [f.name]: (e.value === null || e.value === undefined) ? null : Number(e.value)
                                  }))
                                }
                              />
                              {hasPriceListCalc && (
                                <Button
                                  type="button"
                                  icon={priceCalcLoading['new'] ? "pi pi-spin pi-spinner" : "pi pi-dollar"}
                                  disabled={!!priceCalcLoading['new']}
                                  className="p-button-secondary"
                                  onClick={() => applyPriceListCalculator('new')}
                                  tooltip={t('priceListCalculator')}
                                  tooltipOptions={{ position: 'top' }}
                                />
                              )}
                            </div>
                          </td>
                        );
                      }
                      if (f.type === 'select') return (
                        <td key={f.name}>
                          <Dropdown className="w-100"
                            value={prodRow[f.name]}
                            options={window.App.sortDropdownOptions(itemOptions[f.name] || [], "name")}
                            optionLabel="name"
                            optionValue="id"
                            emptyMessage={t('noSelectableItemYet')}
                            onChange={(e) => setProdRow(prev => applyTechnologyDefaultUnit(
                              prev,
                              f.name,
                              e.target.value,
                              itemOptions.technology || []
                            ))}
                          />
                        </td>
                      )
                      return <td key={f.name}></td>
                    })}
                    <td className={"p-text-right"}>
                      <Button type="button" icon="pi pi-plus" className="p-button-success"
                        // disabled={Object.keys(prodRow).length !== (schema?.productionItems?.itemForm?.fields?.length || 0)}
                        disabled={!isRowValidForAdd(prodRow, schema?.productionItems?.itemForm?.fields)}
                        onClick={() => {
                          const next = [...(formData.production_items || []), prodRow];
                          setProductionItemsState(next);
                          setProdRow({});
                        }}
                      />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </>
            )}
            {schema?.materials?.itemForm &&
              <>
                <div className={"p-grid p-mt-1"}>
                  <div className={"p-col-12 p-lg-6 p-xl-6"}>
                    <h3 className={"p-m-0 p-p-0"}>{schema?.materials?.itemForm?.title || 'Anyagok'}</h3>
                  </div>
                </div>
                <div style={{overflowX: 'auto'}}>
                  <table className={"p-mt-0 w-100"} cellPadding={'5px'} cellSpacing={0} style={{minWidth: '900px'}}>
                    <tr>
                      {getEditableFields(schema?.materials?.itemForm?.fields)?.map(f => <th key={f.name} style={f.name === 'material' ? { minWidth: '260px', width: '300px' } : {}}>{requiredLabel(f)}</th>)}
                      <th></th>
                    </tr>
                    {formData.materials && formData.materials[0] ? (
                      formData.materials.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {getEditableFields(schema?.materials?.itemForm?.fields)?.map((f) => {
                            if (f.type === 'bool') {
                              return (
                                <td key={f.name} className="p-text-center">
                                  <Checkbox
                                    inputId={`material-${rowIndex}-${f.name}`}
                                    checked={!!row[f.name]}
                                    onChange={(e) => {
                                      const next = [...formData.materials];

                                      next[rowIndex] = {
                                        ...next[rowIndex],
                                        [f.name]: e.checked
                                      };

                                      setFormData(prev => ({
                                        ...prev,
                                        materials: next
                                      }));

                                      setNewFormData(prev => ({
                                        ...prev,
                                        materials: next
                                      }));
                                    }}
                                  />
                                </td>
                              );
                            }
                            if (f.type === 'number') return (
                              <td key={f.name}>
                                <InputNumber
                                  mode="decimal"
                                  minFractionDigits={0}
                                  maxFractionDigits={2}
                                  className="w-100"
                                  value={getMaterialFieldValue(row, f.name)}
                                  placeholder={f.placeholder}
                                  onChange={(e) => {
                                    const v = (e.value === null || e.value === undefined || e.value === '') ? null : e.value;
                                    const next = [...formData.materials];
                                    next[rowIndex] = setMaterialFieldValue(next[rowIndex], f.name, v);
                                    setFormData(prev => ({...prev, materials: next}));
                                    setNewFormData(prev => ({...prev, materials: next}));
                                  }}
                                />
                              </td>
                            )
                            if (f.type === 'select' && f.name === 'material') return (
                              <td key={f.name}>
                                <div className="p-inputgroup">
                                  <Button type="button" icon="pi pi-search"
                                    className="p-button-primary"
                                    onClick={() => openMaterialSearch(rowIndex)}
                                  />
                                  <AutoComplete
                                    value={materialDisplay[rowIndex] ?? ''}
                                    suggestions={materialSuggestions}
                                    completeMethod={searchMaterials}
                                    field="name"
                                    appendTo={document.body}
                                    minLength={1}
                                    delay={250}
                                    className="w-100"
                                    placeholder={t('materialSearchPlaceholder')}onChange={(e) => {
                                      setMaterialDisplay(prev => ({...prev, [rowIndex]: e.value}));
                                      if (!e.value) {
                                        const next = [...formData.materials];
                                        next[rowIndex] = {...next[rowIndex], material: null};
                                        setFormData({...formData, materials: next});
                                        setNewFormData({...newFormData, materials: next});
                                      }
                                    }}
                                    onSelect={(e) => {
                                      const picked = e.value;
                                      setMaterialDisplay(prev => ({...prev, [rowIndex]: picked.name}));
                                      const next = [...formData.materials];
                                      next[rowIndex] = {...next[rowIndex], material: picked.id};
                                      setFormData({...formData, materials: next});
                                      setNewFormData({...newFormData, materials: next});
                                    }}
                                  />
                                </div>
                              </td>
                            )
                            if (f.type === 'select') return (
                              <td key={f.name}>
                                <Dropdown className="w-100"
                                  value={row[f.name]}
                                  options={window.App.sortDropdownOptions(
                                    materialOptions[f.name] || [],
                                    f?.options?.labelField || "name"
                                  )}
                                  optionLabel={f?.options?.labelField || "name"}
                                  optionValue="id"
                                  emptyMessage={t('noSelectableItemYet')}
                                  onChange={(e) => {
                                    const next = [...formData.materials];
                                    next[rowIndex] = {...next[rowIndex], [f.name]: e.target.value};
                                    setFormData(prev => ({...prev, materials: next}));
                                    setNewFormData(prev => ({...prev, materials: next}));
                                  }}
                                />
                              </td>
                            )
                            if (f.type === 'string') return (
                              <td key={f.name}>
                                <InputText className="w-100"
                                  value={row[f.name] || ''}
                                  placeholder={f.placeholder}
                                  onChange={(e) => {
                                    const next = [...formData.materials];
                                    next[rowIndex] = {...next[rowIndex], [f.name]: e.target.value};
                                    setFormData(prev => ({...prev, materials: next}));
                                    setNewFormData(prev => ({...prev, materials: next}));
                                  }}
                                />
                              </td>
                            )
                            return <td key={f.name}></td>
                          })}
                          <td className="p-text-right">
                            <Button type="button" icon="pi pi-trash" className="p-button-danger"
                              onClick={() => {
                                const next = [...(formData.materials || [])];
                                next.splice(rowIndex, 1);
                                setFormData(prev => ({...prev, materials: next}));
                                setNewFormData(prev => ({...prev, materials: next}));
                              }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={(getEditableFields(schema?.materials?.itemForm?.fields)?.length || 1)} className={"p-text-center"}>
                          Még nincs tétel. Adj hozzá új tételt!
                        </td>
                        <td></td>
                      </tr>
                    )}
                    <tfoot className={"add"}>
                      <tr className={"add-spacer"}>
                        {getEditableFields(schema?.materials?.itemForm?.fields)?.map(f => <td key={f.name}><i className={"pi pi-arrow-up"} /></td>)}
                        <td></td>
                      </tr>
                      <tr className={"add-form"}>
                        {getEditableFields(schema?.materials?.itemForm?.fields)?.map((f) => {
                          if (f.type === 'bool') {
                            return (
                              <td key={f.name} className="p-text-center">
                                <Checkbox
                                  inputId={`material-new-${f.name}`}
                                  checked={!!matRow[f.name]}
                                  onChange={(e) => {
                                    setMatRow(prev => ({
                                      ...prev,
                                      [f.name]: e.checked
                                    }));
                                  }}
                                />
                              </td>
                            );
                          }
                          if (f.type === 'number') return (
                            <td key={f.name}>
                              <InputNumber
                                mode="decimal"
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                className="w-100"
                                value={matRow[f.name]}
                                placeholder={f.placeholder}
                                onChange={(e) => setMatRow(prev => ({...prev, [f.name]: (e.value === null || e.value === undefined || e.value === '') ? null : e.value}))}
                              />
                            </td>
                          )
                          if (f.type === 'select' && f.name === 'material') return (
                            <td key={f.name}>
                              <div className="p-inputgroup">
                                <Button type="button" icon="pi pi-search"
                                  className="p-button-primary"
                                  onClick={() => openMaterialSearch('new')}
                                />
                                <AutoComplete
                                  value={materialDisplay['new'] || ''}
                                  suggestions={materialSuggestions}
                                  completeMethod={searchMaterials}
                                  field="name"
                                  minLength={1}
                                  delay={250}
                                  className="w-100"
                                  placeholder={t('materialSearchPlaceholder')}onChange={(e) => {
                                    setMaterialDisplay(prev => ({...prev, ['new']: e.value}));
                                    if (!e.value) setMatRow(prev => ({...prev, material: null}));
                                  }}
                                  onSelect={(e) => {
                                    const picked = e.value;
                                    setMaterialDisplay(prev => ({...prev, ['new']: picked.name}));
                                    setMatRow(prev => ({...prev, material: picked.id}));
                                  }}
                                />
                              </div>
                            </td>
                          )
                          if (f.type === 'select') return (
                            <td key={f.name}>
                              <Dropdown className="w-100"
                                value={matRow[f.name]}
                                options={window.App.sortDropdownOptions(materialOptions[f.name] || [], "name")}
                                optionLabel="name"
                                optionValue="id"
                                emptyMessage={t('noSelectableItemYet')}
                                onChange={(e) => setMatRow(prev => ({...prev, [f.name]: e.target.value}))}
                              />
                            </td>
                          )
                          if (f.type === 'string') return (
                            <td key={f.name}>
                              <InputText className="w-100"
                                value={matRow[f.name] || ''}
                                placeholder={f.placeholder}
                                onChange={(e) => setMatRow(prev => ({...prev, [f.name]: e.target.value}))}
                              />
                            </td>
                          )
                          return <td key={f.name}></td>
                        })}
                        <td className="p-text-right">
                          <Button type="button" icon="pi pi-plus" className="p-button-success"
                            // disabled={Object.keys(matRow).length !== (schema?.materials?.itemForm?.fields?.length || 0)}
                            disabled={!isRowValidForAdd(matRow, schema?.materials?.itemForm?.fields)}
                            onClick={() => {
                              const newIndex = (formData.materials || []).length;
                              const pickedLabel = materialDisplay['new'] || '';
                              const next = [...(formData.materials || []), matRow];
                              setFormData(prev => ({...prev, materials: next}));
                              setNewFormData(prev => ({...prev, materials: next}));
                              setMaterialDisplay(prev => {
                                const copy = {...prev};
                                if (pickedLabel) copy[newIndex] = pickedLabel;
                                copy['new'] = '';
                                return copy;
                              });
                              setMatRow({});
                            }}
                          />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            }
            <div className={"p-grid p-mt-1"}>
              <div className={"p-col-12 p-lg-6 p-xl-6"}></div>
              <div className={"p-col-12 p-lg-6 p-xl-6 p-text-right p-col-align-center"}>
                <Button type="submit" label={t('saveAndBack')} loading={loading} icon={"pi pi-arrow-left"} className="p-button-success p-ml-2"/>
              </div>
            </div>
          </form>
        )}/>
      }
    </div>
  )
}

export default withTranslation('common')(OfferitemFormComponent);
